import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { googleProvider } from '../firebase';
import SEO from '../components/SEO';
import { CookieSettingsButton } from '../components/CookieConsent';
import { usePlans } from '../hooks/usePlans';



export default function Home({ consent }: { consent: any }) {
  const [user, setUser] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
  const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(setUser);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => { unsub(); window.removeEventListener('scroll', onScroll); };
  }, []);

  const handleSignIn = async () => {
    try { await signInWithPopup(auth, googleProvider); } catch (e) { console.error(e); }
  };

  const { plans: apiPlans } = usePlans();

  /* ── data ── */
  const channels = [
    { icon: '💬', name: 'WhatsApp', desc: "Chat directly with customers on the world's most popular messaging app." },
    { icon: '📘', name: 'Facebook', desc: 'Reply automatically to messages on your Facebook business page.' },
    { icon: '🌐', name: 'Website', desc: 'Add a chat bubble to your site — visitors get answers in seconds.' },
    { icon: '🎙️', name: 'Voice Bot', desc: 'An AI voice assistant that talks to visitors on your website.' },
  ];

  const useCases = [
    { icon: '📅', title: 'Take bookings 24/7', desc: 'Customers pick a date, the bot confirms it. No back-and-forth, no phone tag.' },
    { icon: '💬', title: 'Answer common questions', desc: '"What are your hours?" "How much does it cost?" — your bot handles it every time.' },
    { icon: '📍', title: 'Share your location & menu', desc: 'Address, opening times, price lists — all sent automatically, instantly.' },
    { icon: '🛒', title: 'Capture leads while you sleep', desc: 'Someone messages at midnight? Your bot replies and saves their details.' },
    { icon: '🔔', title: 'Send reminders & updates', desc: 'Appointment reminders, order updates, promotions — push messages to customers.' },
    { icon: '⭐', title: 'Collect reviews', desc: 'After a service, the bot asks for a review and shares your Google link.' },
  ];

  const planMeta: Record<string, { desc: string; features: string[]; cta: string; popular: boolean; trial?: boolean }> = {
    starter: {
      desc: "Perfect if you're just getting started",
      features: ['1 bot (WhatsApp or website)', '500 conversations/month', 'Pre-built reply templates', 'Basic analytics', 'Email support'],
      cta: 'Get started', popular: false,
    },
    business: {
      desc: 'For businesses that are growing fast',
      features: ['7-day free trial — card required, not charged until day 7', '3 bots (any channel)', '2,000 conversations/month', 'WhatsApp & Facebook Messenger bots', 'AI-powered smart replies', 'Advanced analytics', 'Priority support'],
      cta: 'Start free trial', popular: true, trial: true,
    },
    agency: {
      desc: 'For agencies managing multiple clients',
      features: ['10 bots (any channel)', '10,000 conversations/month', 'White label — your brand', 'Voice bot included', 'API access', 'Dedicated support manager', 'Custom integrations'],
      cta: 'Talk to us', popular: false,
    },
  };

  const plans = ['starter', 'business', 'agency'].map(id => {
    const api = apiPlans.find(p => p.id === id);
    return {
      id,
      name: api?.name ?? id,
      price:      api?.monthly  ?? 0,
      yearlyPrice: api?.yearly  ?? 0,
      setupFee:   api?.setupFee ?? 0,
      ...planMeta[id],
    };
  });

  const testimonials = [
    { name: 'Takudzwa M.', biz: 'Webiconic', text: "Since setting up the WhatsApp bot, my phone isn't ringing every 5 minutes with the same questions. It handles everything and I just deal with the actual bookings." },
    { name: 'Rutendo S.', biz: 'Clothing Store, Harare', text: 'I was sceptical but it was so easy to set up. Our customers love it — they get replies instantly even when we\'re closed.' },
    { name: 'Tafadzwa K.', biz: 'Real Estate Agent', text: 'People send me property enquiries at all hours. The bot collects their details and sends them listings overnight. I follow up in the morning.' },
  ];

  const faqs = [
    { q: 'Do I need any tech skills to use this?', a: "None at all. You pick your industry, answer a few questions, and we set everything up. Most people are live within a day." },
    { q: 'Will I need to buy a new phone number for WhatsApp?', a: "No — we can use your existing business number. We just register it with WhatsApp's business system on your behalf." },
    { q: 'Can I customise what the bot says?', a: 'Yes. Every reply is editable in our visual flow builder — no coding, just click and type. You can add new responses any time.' },
    { q: "What if customers ask something the bot doesn't know?", a: "The bot can hand the conversation over to you, send a \"we'll get back to you\" message, or collect their details for follow-up." },
    { q: 'Is there a contract?', a: 'No long-term contract. Pay month to month and cancel any time — though most customers stick around because it just works.' },
    { q: 'How quickly can I get started?', a: 'WhatsApp bots are usually live within 1–3 business days (Meta verification). Website and Facebook bots can go live the same day.' },
  ];

  const fonts = "'DM Sans', system-ui, -apple-system, sans-serif";

  /* ── responsive helpers (inline media queries via JS) ── */
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <>
      <SEO
        title={`${companyName} | Your business, always open`}
        description={`${companyName} lets any business set up a WhatsApp, website, or Facebook chatbot in minutes — no tech skills needed.`}
        keywords="WhatsApp bot Zimbabwe, chatbot, business automation, customer service bot, Facebook bot, website chat"
        type="website"
      />

      <style>{`
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }

        .nav-links { display: flex; gap: 28px; align-items: center; }
        .hamburger { display: none; }
        .mobile-menu { display: none; }

        .hero-btns { flex-direction: row; }
        .hero-stats { flex-direction: row; gap: 60px; }

        .grid-4 { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
        .grid-3 { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
        .grid-2 { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
        .steps-grid { grid-template-columns: repeat(3, 1fr); }
        .pricing-grid { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
        .footer-grid { grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }

        .section-pad { padding: 80px 40px; }
        .hero-pad { padding: 120px 20px 80px; }

        @media (max-width: 768px) {
          .nav-links { display: none; }
          .hamburger { display: flex; align-items: center; justify-content: center; background: none; border: none; cursor: pointer; padding: 8px; }
          .mobile-menu { display: flex; flex-direction: column; position: fixed; top: 60px; left: 0; right: 0; background: rgba(15,8,32,0.97); backdrop-filter: blur(12px); padding: 20px 24px 28px; gap: 4px; z-index: 999; border-bottom: 1px solid rgba(255,255,255,0.08); }
          .mobile-menu a, .mobile-menu button { display: block; width: 100%; text-align: left; padding: 12px 0; font-size: 16px; font-weight: 500; color: rgba(255,255,255,0.85); text-decoration: none; border: none; background: none; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.06); font-family: inherit; }
          .mobile-menu a:last-child, .mobile-menu button:last-child { border-bottom: none; }
          .mobile-cta { margin-top: 12px; background: #5B4FE8 !important; color: white !important; padding: 12px 0 !important; border-radius: 10px !important; text-align: center !important; font-weight: 700 !important; border-bottom: none !important; }

          .hero-btns { flex-direction: column; align-items: center; }
          .hero-btns a, .hero-btns button { width: 100%; max-width: 320px; text-align: center; }
          .hero-stats { flex-direction: row; gap: 20px; justify-content: center; }
          .hero-stats > div { flex: 1; min-width: 0; }

          .steps-grid { grid-template-columns: 1fr; gap: 32px; }
          .grid-4 { grid-template-columns: repeat(2, 1fr); }
          .grid-3 { grid-template-columns: 1fr; }
          .grid-2 { grid-template-columns: 1fr; }
          .pricing-grid { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; }
          .footer-grid { grid-template-columns: 1fr 1fr; gap: 28px; }

          .section-pad { padding: 56px 20px; }
          .hero-pad { padding: 100px 20px 60px; }

          .section-title { font-size: 28px !important; }
          .section-sub { font-size: 14px !important; }
          .hero-title { font-size: clamp(30px, 8vw, 44px) !important; }
          .hero-sub { font-size: 15px !important; }
          .stat-num { font-size: 22px !important; }
          .stat-label { font-size: 10px !important; }

          .footer-bottom { flex-direction: column; align-items: flex-start; gap: 8px; }
          .plan-card { padding: 24px 20px !important; }
          .testimonial-card { padding: 20px !important; }
        }

        @media (max-width: 420px) {
          .grid-4 { grid-template-columns: 1fr; }
          .hero-stats { gap: 12px; }
        }
      `}</style>

      <div style={{ fontFamily: fonts, overflowX: 'hidden', background: 'white' }}>

        {/* ═══ NAVBAR ═══ */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          padding: '0 24px', height: '60px',
          background: scrolled || menuOpen ? 'rgba(15,8,32,0.97)' : 'transparent',
          backdropFilter: scrolled || menuOpen ? 'blur(10px)' : 'none',
          boxShadow: scrolled ? '0 1px 0 rgba(0,0,0,0.12)' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          transition: 'all 0.3s',
        }}>
          <img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} />

          {/* Desktop nav */}
          <div className="nav-links">
            {[
              { href: '/about', label: 'About' },
              { href: '/how-it-works', label: 'How it works' },
              { href: '/pricing', label: 'Pricing' },
              { href: '#faq', label: 'FAQ' },
              { href: '/documentation', label: 'Docs' },
              { href: '/contact', label: 'Contact' },
            ].map(({ href, label }) => (
              href.startsWith('/') ? (
                <Link key={label} to={href} style={{ textDecoration: 'none', color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: '500' }}>{label}</Link>
              ) : (
                <a key={label} href={href} style={{ textDecoration: 'none', color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: '500' }}>{label}</a>
              )
            ))}
            {user ? (
              <Link to="/dashboard" style={{ background: '#5B4FE8', color: 'white', padding: '8px 20px', borderRadius: '20px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
                Dashboard →
              </Link>
            ) : (
              <Link to="/register" style={{ background: '#5B4FE8', color: 'white', padding: '8px 20px', borderRadius: '20px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
                Get started
              </Link>
            )}
          </div>

          {/* Hamburger */}
          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              {menuOpen ? (
                <path d="M4 4L18 18M18 4L4 18" stroke="white" strokeWidth="2" strokeLinecap="round" />
              ) : (
                <path d="M3 6h16M3 11h16M3 16h16" stroke="white" strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </button>
        </nav>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="mobile-menu">
            {[{ href: '/how-it-works', label: 'How it works' }, { href: '#pricing', label: 'Pricing' }, { href: '#faq', label: 'FAQ' }].map(({ href, label }) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}</a>
            ))}
            {user ? (
              <Link to="/dashboard" className="mobile-cta" onClick={() => setMenuOpen(false)}>Dashboard →</Link>
            ) : (
              <Link to="/register" className="mobile-cta" onClick={() => setMenuOpen(false)}>Get started free</Link>
            )}
          </div>
        )}

        {/* ═══ HERO ═══ */}
        <div className="hero-pad" style={{
          minHeight: '100vh',
          background: 'linear-gradient(150deg, #1a1040 0%, #0c0820 60%, #111827 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', color: 'white',
        }}>
          <div style={{
            display: 'inline-block', background: 'rgba(91,79,232,0.2)', border: '1px solid rgba(91,79,232,0.4)',
            color: '#a5b4fc', fontSize: '11px', fontWeight: '600', padding: '5px 14px',
            borderRadius: '20px', letterSpacing: '0.06em', marginBottom: '24px',
          }}>
            YOUR BUSINESS, ALWAYS OPEN
          </div>

          <h1 className="hero-title" style={{ fontSize: 'clamp(32px, 6vw, 60px)', fontWeight: '800', lineHeight: 1.15, marginBottom: '18px', letterSpacing: '-0.03em', maxWidth: '780px' }}>
            Never miss a customer<br />
            <span style={{ color: '#818cf8' }}>— even while you sleep</span>
          </h1>

          <p className="hero-sub" style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'rgba(255,255,255,0.65)', maxWidth: '520px', lineHeight: 1.7, marginBottom: '32px', padding: '0 8px' }}>
            Set up a bot that chats with your customers on WhatsApp, your website, or Facebook. It answers questions, takes bookings, and sends prices — automatically, 24/7.
          </p>

          <div className="hero-btns" style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '48px', width: '100%', maxWidth: '480px', padding: '0 16px' }}>
            {!user ? (
              <Link to="/register" style={{
                background: '#5B4FE8', color: 'white', padding: '14px 28px',
                borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '700',
                boxShadow: '0 0 40px rgba(91,79,232,0.4)', flex: 1, minWidth: '160px',
              }}>
                Get started free →
              </Link>
            ) : (
              <Link to="/dashboard" style={{
                background: '#5B4FE8', color: 'white', padding: '14px 28px',
                borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '700',
                flex: 1, minWidth: '160px',
              }}>
                Go to dashboard →
              </Link>
            )}
            <a href="/how-it-works" style={{
              background: 'rgba(255,255,255,0.08)', color: 'white', padding: '14px 28px',
              borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '600',
              border: '1px solid rgba(255,255,255,0.15)', flex: 1, minWidth: '160px',
            }}>
              See how it works
            </a>
          </div>

          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginBottom: '48px' }}>
            No credit card required · Cancel any time · Setup in minutes
          </div>

          {/* Stats */}
          <div className="hero-stats" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '32px', borderTop: '1px solid rgba(255,255,255,0.08)', width: '100%', maxWidth: '560px', padding: '32px 16px 0' }}>
            {[
              { num: '500+', label: 'Businesses on autopilot' },
              { num: '100k+', label: 'Chats handled' },
              { num: '24/7', label: 'Always online' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                <div className="stat-num" style={{ fontSize: '26px', fontWeight: '800', color: 'white' }}>{s.num}</div>
                <div className="stat-label" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '3px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ CHANNELS ═══ */}
        <div className="section-pad" style={{ background: '#f8f7ff' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', marginBottom: '10px', letterSpacing: '-0.02em' }}>
                Works on the channels your customers already use
              </h2>
              <p className="section-sub" style={{ color: '#64748b', fontSize: '15px', maxWidth: '480px', margin: '0 auto' }}>
                No need to teach customers anything new. Your bot meets them where they already are.
              </p>
            </div>
            <div className="grid-4" style={{ display: 'grid', gap: '16px' }}>
              {channels.map((c, i) => (
                <div key={i} style={{ background: 'white', borderRadius: '14px', padding: '22px 20px', border: '1px solid #e8e4ff' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>{c.icon}</div>
                  <div style={{ color: '#1e293b', fontWeight: '700', fontSize: '15px', marginBottom: '5px' }}>{c.name}</div>
                  <div style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.6' }}>{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ HOW IT WORKS ═══ */}
        <div id="how-it-works" className="section-pad" style={{ background: 'white' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 className="section-title" style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', marginBottom: '10px', letterSpacing: '-0.02em' }}>
                Set up in 3 simple steps
              </h2>
              <p className="section-sub" style={{ color: '#64748b', fontSize: '15px' }}>
                No technical background needed. If you can fill in a form, you can set up a bot.
              </p>
            </div>

            <div className="steps-grid" style={{ display: 'grid', gap: '28px' }}>
              {[
                { step: '1', icon: '🏢', title: 'Tell us about your business', desc: 'Your business name, industry, and which channel you want to use. Takes about 2 minutes.' },
                { step: '2', icon: '✨', title: 'Pick a template', desc: "We pre-fill your bot with smart replies for your industry. A barber shop gets booking flows, a restaurant gets a menu, and so on." },
                { step: '3', icon: '🚀', title: "You're live", desc: "For websites and Facebook, you're live instantly. For WhatsApp, we register your number with Meta — usually 1–3 days." },
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#5B4FE8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '800', marginBottom: '14px', flexShrink: 0 }}>
                    {s.step}
                  </div>
                  <div style={{ fontSize: '26px', marginBottom: '8px' }}>{s.icon}</div>
                  <div style={{ color: '#1e293b', fontWeight: '700', fontSize: '16px', marginBottom: '6px' }}>{s.title}</div>
                  <div style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ USE CASES ═══ */}
        <div className="section-pad" style={{ background: '#0f172a' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{ fontSize: '32px', fontWeight: '800', color: 'white', marginBottom: '10px', letterSpacing: '-0.02em' }}>
                What your bot can do for you
              </h2>
              <p className="section-sub" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px' }}>
                Real things real businesses automate every day with {companyName}
              </p>
            </div>
            <div className="grid-2" style={{ display: 'grid', gap: '14px' }}>
              {useCases.map((u, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '13px', padding: '20px' }}>
                  <div style={{ fontSize: '26px', marginBottom: '10px' }}>{u.icon}</div>
                  <div style={{ color: 'white', fontWeight: '600', fontSize: '14px', marginBottom: '5px' }}>{u.title}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: '1.6' }}>{u.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ TESTIMONIALS ═══ */}
        <div className="section-pad" style={{ background: 'white' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', marginBottom: '8px', letterSpacing: '-0.02em' }}>
                Businesses that made the switch
              </h2>
              <p className="section-sub" style={{ color: '#64748b', fontSize: '15px' }}>From Harare to the world</p>
            </div>
            <div className="grid-3" style={{ display: 'grid', gap: '20px' }}>
              {testimonials.map((t, i) => (
                <div key={i} className="testimonial-card" style={{ background: '#f8f7ff', borderRadius: '14px', padding: '24px', border: '1px solid #e8e4ff' }}>
                  <div style={{ fontSize: '16px', marginBottom: '12px' }}>⭐⭐⭐⭐⭐</div>
                  <p style={{ color: '#334155', fontSize: '14px', lineHeight: '1.7', marginBottom: '16px', fontStyle: 'italic' }}>
                    "{t.text}"
                  </p>
                  <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{t.name}</div>
                  <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{t.biz}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ PRICING ═══ */}
        <div id="pricing" className="section-pad" style={{ background: '#f8f7ff' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', marginBottom: '8px', letterSpacing: '-0.02em' }}>
                Simple, honest pricing
              </h2>
              <p className="section-sub" style={{ color: '#64748b', fontSize: '15px', marginBottom: '22px' }}>
                No hidden fees. No long contracts. Cancel any time.
              </p>

              {/* Toggle */}
              <div style={{ display: 'inline-flex', background: 'white', border: '1px solid #e5e7eb', borderRadius: '24px', padding: '4px', gap: '4px' }}>
                {(['monthly', 'yearly'] as const).map(b => (
                  <button key={b} onClick={() => setSelectedBilling(b)} style={{
                    padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                    background: selectedBilling === b ? '#5B4FE8' : 'transparent',
                    color: selectedBilling === b ? 'white' : '#64748b',
                    fontSize: '13px', fontWeight: '600', transition: 'all 0.2s', position: 'relative',
                    fontFamily: 'inherit',
                  }}>
                    {b === 'yearly' ? 'Yearly' : 'Monthly'}
                    {b === 'yearly' && (
                      <span style={{ position: 'absolute', top: '-10px', right: '-14px', background: '#f59e0b', color: 'white', fontSize: '9px', padding: '2px 6px', borderRadius: '10px', fontWeight: '800' }}>
                        -20%
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="pricing-grid" style={{ display: 'grid', gap: '20px' }}>
              {plans.map(plan => {
                const price = selectedBilling === 'monthly' ? plan.price : (plan.yearlyPrice || Math.round(plan.price * 12 * 0.8));
                return (
                  <div key={plan.id} className="plan-card" style={{
                    background: 'white', borderRadius: '18px', padding: '28px',
                    border: plan.popular ? '2px solid #5B4FE8' : '1px solid #e5e7eb',
                    boxShadow: plan.popular ? '0 16px 40px rgba(91,79,232,0.12)' : 'none',
                    position: 'relative',
                  }}>
                    {plan.popular && (
                      <div style={{
                        position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                        background: '#5B4FE8', color: 'white', padding: '4px 16px',
                        borderRadius: '20px', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap',
                      }}>
                        Most popular
                      </div>
                    )}
                    {(plan as any).trial && (
                      <div style={{ position: 'absolute', top: '16px', right: '-4px', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', fontSize: '9px', fontWeight: '800', padding: '4px 10px 4px 12px', borderRadius: '20px 0 0 20px', letterSpacing: '0.06em', boxShadow: '0 2px 8px rgba(16,185,129,0.3)' }}>
                        🎁 7-DAY FREE TRIAL
                      </div>
                    )}
                    <div style={{ fontWeight: '800', fontSize: '20px', color: '#1e293b', marginBottom: '4px' }}>{plan.name}</div>
                    <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '18px' }}>{plan.desc}</div>
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontSize: '36px', fontWeight: '800', color: '#1e293b' }}>${price}</span>
                      <span style={{ color: '#64748b', fontSize: '14px' }}>/{selectedBilling === 'monthly' ? 'month' : 'year'}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '20px' }}>
                      + one-time setup fee: <strong>${plan.setupFee}</strong>
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                      {plan.features.map((f, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#334155' }}>
                          <span style={{ color: '#5B4FE8', fontWeight: '700', flexShrink: 0 }}>✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>

                    {plan.id === 'agency' ? (
                      <Link to="/contact" style={{
                        display: 'block', padding: '12px', background: '#1e293b', color: 'white',
                        textAlign: 'center', borderRadius: '30px', textDecoration: 'none',
                        fontWeight: '700', fontSize: '14px',
                      }}>
                        {plan.cta}
                      </Link>
                    ) : (
                      <button onClick={handleSignIn} style={{
                        width: '100%', padding: '12px',
                        background: plan.popular ? '#5B4FE8' : 'transparent',
                        color: plan.popular ? 'white' : '#5B4FE8',
                        border: plan.popular ? 'none' : '2px solid #5B4FE8',
                        borderRadius: '30px', fontWeight: '700', cursor: 'pointer', fontSize: '14px',
                        transition: 'all 0.2s', fontFamily: 'inherit',
                      }}>
                        {plan.cta}
                      </button>
                    )}
                    {(plan as any).trial && (
                      <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '11px', color: '#64748b' }}>
                        Card required · Cancel before day 7 to pay nothing
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ textAlign: 'center', marginTop: '28px', fontSize: '13px', color: '#64748b' }}>
              Not sure which plan? <a href={`mailto:${companyEmail}`} style={{ color: '#5B4FE8', textDecoration: 'none', fontWeight: '600' }}>Chat to us</a> — we'll help.
            </div>
          </div>
        </div>

        {/* ═══ FAQ ═══ */}
        <div id="faq" className="section-pad" style={{ background: 'white' }}>
          <div style={{ maxWidth: '680px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', letterSpacing: '-0.02em' }}>
                Common questions
              </h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {faqs.map((faq, i) => (
                <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{
                      width: '100%', padding: '16px 18px', background: openFaq === i ? '#f8f7ff' : 'white',
                      border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', textAlign: 'left', fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '14px', lineHeight: '1.4', flex: 1, paddingRight: '12px' }}>{faq.q}</span>
                    <span style={{ color: '#5B4FE8', fontSize: '18px', flexShrink: 0, transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0)', display: 'inline-block' }}>+</span>
                  </button>
                  {openFaq === i && (
                    <div style={{ padding: '0 18px 16px', background: '#f8f7ff', color: '#475569', fontSize: '14px', lineHeight: '1.7' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ CTA ═══ */}
        <div className="section-pad" style={{
          background: 'linear-gradient(135deg, #4338ca 0%, #5B4FE8 50%, #7c3aed 100%)',
          textAlign: 'center', color: 'white',
        }}>
          <div style={{ fontSize: '44px', marginBottom: '14px' }}>🚀</div>
          <h2 className="section-title" style={{ fontSize: '32px', fontWeight: '800', marginBottom: '10px', letterSpacing: '-0.02em' }}>
            Ready to stop missing customers?
          </h2>
          <p className="section-sub" style={{ fontSize: '15px', opacity: 0.85, maxWidth: '460px', margin: '0 auto 28px', lineHeight: '1.6' }}>
            Join 500+ Zimbabwean businesses that run 24/7 without answering the same questions all day.
          </p>
          {!user ? (
            <Link to="/register" style={{
              display: 'inline-block', background: 'white', color: '#5B4FE8',
              padding: '14px 32px', borderRadius: '30px', textDecoration: 'none',
              fontSize: '15px', fontWeight: '800',
            }}>
              Get started for free →
            </Link>
          ) : (
            <Link to="/dashboard" style={{
              display: 'inline-block', background: 'white', color: '#5B4FE8',
              padding: '14px 32px', borderRadius: '30px', textDecoration: 'none',
              fontSize: '15px', fontWeight: '800',
            }}>
              Go to my dashboard →
            </Link>
          )}
          <div style={{ marginTop: '12px', fontSize: '12px', opacity: 0.6 }}>
            No credit card needed · Cancel any time
          </div>
        </div>


        {/* ═══ FOOTER ═══ */}
        <footer className="section-pad" style={{ background: '#0f172a', color: 'rgba(255,255,255,0.45)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div className="footer-grid" style={{ display: 'grid', gap: '36px', marginBottom: '0' }}>
              <div>
                <img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain', marginBottom: '8px' }} />
                <div style={{ fontSize: '13px', lineHeight: '1.7' }}>
                  Helping Zimbabwean businesses chat with their customers 24/7 — without lifting a finger.
                </div>
                <div style={{ marginTop: '10px', fontSize: '13px' }}>
                  <a href={`mailto:${companyEmail}`} style={{ color: '#818cf8', textDecoration: 'none' }}>{companyEmail}</a>
                </div>
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Bots</div>
                {['WhatsApp Bot', 'Facebook Bot', 'Website Chat', 'Voice Bot'].map(item => (
                  <div key={item} style={{ fontSize: '13px', marginBottom: '7px' }}>{item}</div>
                ))}
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Company</div>
                {[
                  { label: 'Pricing', href: '#pricing' },
                  { label: 'FAQ', href: '#faq' },
                  { label: 'Contact', href: '/contact' },
                  { label: 'Blog', href: '/blog' },
                ].map(item => (
                  <div key={item.label} style={{ fontSize: '13px', marginBottom: '7px' }}>
                    <a href={item.href} style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>{item.label}</a>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Legal</div>
                <div style={{ fontSize: '13px', marginBottom: '7px' }}>
                  <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Privacy Policy</Link>
                </div>
                <div style={{ fontSize: '13px', marginBottom: '7px' }}>
                  <Link to="/terms" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Terms of Service</Link>
                </div>
                <div style={{ fontSize: '13px', marginBottom: '7px' }}>
                  <Link to="/acceptable-use" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Acceptable Use</Link>
                </div>
                <div style={{ fontSize: '13px', marginBottom: '7px' }}>
                  <Link to="/cookies" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Cookie Policy</Link>
                </div>
                <div style={{ fontSize: '13px', marginBottom: '7px' }}>

                </div>
              </div>
            </div>
            <CookieSettingsButton consent={consent} />
            <div className="footer-bottom" style={{ marginTop: '36px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', fontSize: '12px' }}>
              <div>© {new Date().getFullYear()} {companyName}. All rights reserved.</div>
              <div style={{ display: 'flex', gap: '20px' }}>
                <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Privacy</Link>
                <Link to="/terms" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Terms</Link>
                <Link to="/acceptable-use" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Use</Link>
                <Link to="/cookies" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Cookies</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}